/**
 * Utility functions for formatting API request errors for tracking.
 * Used to capture error codes and messages in the format:
 * - `<ENDPOINT>:<CODE>:<MESSAGE>` - When status and message available (2xx/4xx only)
 * - `<ENDPOINT>:<CODE>` - When only status available (including 5xx)
 * - `<ENDPOINT>` - When no status available
 */

interface ApiError {
	status?: number;
	response?: {
		status?: number;
		body?: {
			message?: string;
		};
	};
	body?: {
		errors?: Array<{ title?: string; field?: string }>;
		message?: string;
		statusCode?: number;
	};
	message?: string;
}

/**
 * Extract status code from various error shapes.
 * Supports SDK errors, superagent errors, and fetch errors.
 */
export const extractStatusCode = (error: unknown): number | undefined => {
	if (!error || typeof error !== 'object') return undefined;
	const err = error as ApiError;
	return err.status || err.response?.status || err.body?.statusCode;
};

/**
 * Extract error message from various error shapes.
 * Priority: body.errors[0].title(-field) > response.body.message > body.message > error.message
 * When field exists in errors[0], formats as "title-field" (e.g., "invalid_field-email")
 */
export const extractErrorMessage = (error: unknown): string | undefined => {
	if (!error || typeof error !== 'object') return undefined;
	const err = error as ApiError;

	const firstError = err.body?.errors?.[0];
	if (firstError?.title) {
		return firstError.field
			? `${firstError.title}-${firstError.field}`
			: firstError.title;
	}

	return err.response?.body?.message || err.body?.message || err.message;
};

/**
 * Format an API request error for tracking.
 * Only includes error message for 2xx/4xx responses, never for 5xx.
 *
 * @param endpoint - The API endpoint identifier (e.g., 'membership-signup')
 * @param error - The caught error object
 * @returns Formatted error string for tracking
 *
 * @example
 * // With status and message (4xx)
 * formatApiRequestError('membership-signup', { status: 422, body: { errors: [{ title: 'invalid_field' }] } })
 * // Returns: 'membership-signup:422:invalid_field'
 *
 * @example
 * // With status only (5xx)
 * formatApiRequestError('inquiry', { status: 500 })
 * // Returns: 'inquiry:500'
 *
 * @example
 * // No status available
 * formatApiRequestError('membership-login', new Error('Network error'))
 * // Returns: 'membership-login'
 */
export const formatApiRequestError = (
	endpoint: string,
	error: unknown,
): string => {
	const status = extractStatusCode(error);
	const message =
		status && status < 500 ? extractErrorMessage(error) : undefined;

	if (status && message) {
		return `${endpoint}:${status}:${message}`;
	}
	if (status) {
		return `${endpoint}:${status}`;
	}
	return endpoint;
};
